// components/CreateTaskTemplateModal.jsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateTaskTemplateModal = ({ projectTemplateId, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);

  const [formData, setFormData] = useState({
    taskTemplateName: "",
    templateDescription: "",
    defaultPriority: "Medium",
    defaultAssigneeId: "",
    defaultDueDate: "",
    sequenceOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = { ...formData };

      // Clean empty fields
      if (!submitData.defaultAssigneeId) submitData.defaultAssigneeId = null;
      if (!submitData.defaultDueDate) submitData.defaultDueDate = null;
      if (!submitData.templateDescription?.trim())
        submitData.templateDescription = null;

      await apiService.createTaskTemplate(projectTemplateId, submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating task template:", err);
      setError(err.response?.data?.message || "Failed to create task template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            New Task Template
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/20 border border-red-500/30 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="taskTemplateForm">
            <div className="space-y-4">
              {/* Task Name */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <span className="text-red-500">*</span> Task Name
                </label>
                <input
                  type="text"
                  value={formData.taskTemplateName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      taskTemplateName: e.target.value,
                    })
                  }
                  required
                  placeholder="e.g., Broadband Install, Finance Check, System Build"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              {/* Description */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Description
                </label>
                <textarea
                  rows="3"
                  value={formData.templateDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      templateDescription: e.target.value,
                    })
                  }
                  placeholder="Describe what needs to be done..."
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              {/* Priority */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Default Priority
                </label>
                <select
                  value={formData.defaultPriority}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultPriority: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              {/* Default Assignee */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Default Assignee
                </label>
                <select
                  value={formData.defaultAssigneeId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultAssigneeId: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Default Due Date Offset */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Due Date (days from project start)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.defaultDueDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultDueDate: e.target.value,
                    })
                  }
                  placeholder="e.g., 7 for one week after project starts"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
                <p
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Leave empty for no default due date
                </p>
              </div>

              {/* Sequence Order */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Sequence Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.sequenceOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sequenceOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="0"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
                <p
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Lower numbers are created first
                </p>
              </div>

              {/* Active Status */}
              <div>
                <label
                  className={`flex items-center gap-2 text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                        : "border-gray-300 bg-white text-blue-600"
                    }`}
                  />
                  Active
                </label>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="taskTemplateForm"
            disabled={loading || !formData.taskTemplateName}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create Task Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskTemplateModal;
